import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import { Box, Typography } from "@mui/material";
import { SkeletonImage } from "components/loader/SkeletonImage";
import { useIsMobile, useIsTablet } from "hooks/use-breakpoint";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./TrendCarousel.css";
import type { TrendCarouselItem } from "./trendCarousel.types";

const AUTO_SLIDE_MS = 5000;

// Card widths must mirror the breakpoint rules in TrendCarousel.css so the
// slide step lands each card flush against the viewport edge.
const CARD_WIDTH_BY_VISIBLE: Record<number, string> = {
  1: "calc(100% - 16px)",
  2: "calc((100% - 16px) / 2)",
  3: "calc((100% - 32px) / 3)",
};

interface TrendCarouselProps {
  items: TrendCarouselItem[];
}

const TrendCardImage = ({ src, alt }: { src: string; alt: string }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError || !src) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--alias-surface-low, #f5f5f5)",
          color: "var(--alias-text-on-white-disable, #999)",
        }}
        aria-label={alt}
      >
        <BrokenImageIcon sx={{ fontSize: 48 }} />
      </Box>
    );
  }

  return (
    <SkeletonImage
      src={src}
      alt={alt}
      className="trend-card__image"
      width="100%"
      height="100%"
      onError={() => setHasError(true)}
    />
  );
};

export const TrendCarousel = ({ items }: TrendCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const visibleCards = isMobile ? 1 : isTablet ? 2 : 3;
  const maxIndex = Math.max(0, items.length - visibleCards);
  const needsSlider = items.length > visibleCards;

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  useEffect(() => {
    if (!needsSlider) return;
    const timer = setInterval(goToNext, AUTO_SLIDE_MS);
    return () => clearInterval(timer);
  }, [needsSlider, goToNext]);

  // Clamp index when items shrink (e.g., language change reduces item count)
  useEffect(() => {
    setCurrentIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  if (items.length === 0) {
    return null;
  }

  const handleCardClick = (item: TrendCarouselItem) => {
    if (!item.url) return;
    if (item.url.startsWith("http")) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    } else {
      navigate(item.url);
    }
  };

  return (
    <div className="trend-carousel">
      <Typography className="trend-carousel__title">
        {t("ldp_trends_title", "Ahead of the curve, by design.")}
      </Typography>
      <Typography className="trend-carousel__subtitle">
        {t("ldp_trends_subtitle", "Explore our design trend reports")}
      </Typography>

      <div className="trend-carousel__viewport">
        <div className="trend-carousel__track-wrapper">
          <div
            className="trend-carousel__track"
            style={{
              transform: `translateX(calc(-${currentIndex} * (${CARD_WIDTH_BY_VISIBLE[visibleCards]} + 16px)))`,
            }}
          >
            {items.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`trend-card${!item.url ? " trend-card--no-link" : ""}`}
                style={{ flex: `0 0 ${CARD_WIDTH_BY_VISIBLE[visibleCards]}` }}
                onClick={() => handleCardClick(item)}
              >
                <div className="trend-card__image-wrapper">
                  <TrendCardImage src={item.image} alt={item.title} />
                </div>
                <Typography className="trend-card__title">
                  {item.title}
                </Typography>
              </button>
            ))}
          </div>
        </div>
      </div>

      {needsSlider && (
        <div className="trend-carousel__dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              type="button"
              key={`dot-${i}`}
              onClick={() => setCurrentIndex(i)}
              className={`trend-carousel__dot ${i === currentIndex ? "trend-carousel__dot--active" : ""}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
